import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { Link, useHistory, useLocation } from 'react-router-dom'
import ConfettiExplosion from 'react-confetti-explosion'
import { useSelector } from 'react-redux'
import { theseus2 } from '../../../../api/theseus'
import { selectCurrentProject } from '../../../../modules/missionControl/selectors'
import { selectUser } from '../../../../modules/user/selectors'
import { Checkbox } from '../../../../page_components/DropDetails/ChapterView'
import TaskBody from '../../TaskBody'
import { NewTaskType, SubTask, TwitterViewsMetaData } from '../../types'
import AnimatedLoader from '../../../AnimatedLoader'
import { ThemedButton } from '../../../ThemedButton'
import { Box } from '../../../UI/Box'
import routes from '../../../../router/routes'
import humanizeDuration from 'humanize-duration'
import { useRefetchUpdateUser } from '../../../../page_components/TwitterAuth'
import useIsLoggedIn from '../../../../hooks/useIsLoggedIn'
import ConnectWalletButton from '../../../Library/Buttons/ConnectWalletButton'
import TwitterConnectButton from '../../../SocialConnectButtons/Twitter'

type TwitterInfo = {
  profile_image_url: string
  username: string
  verified: boolean
  id: string
  name: string
}

export type Props = {
  subtask: SubTask
  taskId: number
  updateTaskSubmissions: () => Promise<unknown>
  taskAmount: ReactNode
}

const TwitterTask = ({
  subtask,
  taskId,
  updateTaskSubmissions,
  taskAmount,
}: Props) => {
  const isTwitterViewsTask = subtask.newTaskType === NewTaskType.TWITTER_VIEW
  const [twitterInfo, setTwitterInfo] = useState<TwitterInfo | null>(null)
  const { publicName, instructions } = subtask
  const title = publicName ?? 'Follow us on X'
  const description =
    instructions ?? 'Simply press the follow button below to get our updates'
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingFollow, setIsLoadingFollow] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isComplete, setIsComplete] = useState(false)

  const project = useSelector(selectCurrentProject)
  const { twitterHandle } = useSelector(selectUser) ?? {}
  const { isLoggedIn } = useIsLoggedIn()

  const location = useLocation()
  const history = useHistory()
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  )

  const updateSearchParam = useCallback(
    (paramName: string, value: string) => {
      searchParams.set(paramName, value)
      history.push(`${location.pathname}?${searchParams.toString()}`)
    },
    [history, location.pathname, searchParams],
  )

  const closeModal = useCallback(() => {
    updateSearchParam('active_mission', '')
  }, [updateSearchParam])

  function selfDestruct(ms = 2000) {
    setTimeout(closeModal, ms)
  }

  async function follow() {
    try {
      setError(null)
      setIsLoadingFollow(true)
      const { data } = await theseus2.post(`/auth/twitter/follow`, {
        subtaskId: subtask.id,
        projectId: project.id,
        taskId,
      })
      if (!data) throw new Error('Failed to follow')
      const { message } = data
      if (message !== 'success') throw new Error('Failed to follow')
      await updateTaskSubmissions()
      selfDestruct()
      setIsComplete(true)
    } catch (error) {
      console.error(error)
      const theseusError = error?.data?.error
      setError(theseusError ?? 'Server error')
    } finally {
      setIsLoadingFollow(false)
    }
  }

  const fetchTwitterInfo = useCallback(async () => {
    try {
      setIsLoading(true)
      const res = await theseus2.get(`/auth/twitter/task/info/${subtask.id}`)
      const { data } = res
      if (!data?.userToFollow) throw new Error('No user to follow')
      setTwitterInfo(data.userToFollow)
    } catch (error) {
      console.error(error)
      const theseusError = error?.data?.error
      setError(theseusError ?? 'Server error')
    } finally {
      setIsLoading(false)
    }
  }, [subtask.id])

  useEffect(() => {
    if (isTwitterViewsTask) return
    if (!isLoggedIn || !twitterHandle) return
    fetchTwitterInfo()
  }, [fetchTwitterInfo, isLoggedIn, isTwitterViewsTask, twitterHandle])

  function postOnX() {
    const twitterViewsLastAttempted = localStorage.getItem(
      'twitter_views_last_attempted',
    )
    const parsedTwitterViewsLastAttempted = JSON.parse(
      twitterViewsLastAttempted ?? '{}',
    )
    localStorage.setItem(
      'twitter_views_last_attempted',
      JSON.stringify({
        ...parsedTwitterViewsLastAttempted,
        [String(taskId)]: Date.now(),
      }),
    )
    const twitterViewsMetaData = subtask.metaData as TwitterViewsMetaData
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        `${
          twitterViewsMetaData.tweetAutofill ??
          `.@${twitterViewsMetaData.clientHandle}`
        } `,
      )}`,
      '_blank',
    )
  }

  return (
    <TaskBody
      taskId={taskId}
      title={title}
      description={description}
      error={
        error ? (
          error === 'No user to follow' ? (
            'We love your enthusiasm, but you can only initiate 5 follows per 15 minutes, try again later'
          ) : error?.includes('Please try reconnecting your X account.') ? (
            <>
              <span>{error}</span>
              <br />
              <br />
              <Link to={routes.account} style={{ borderBottom: '1px solid' }}>
                Reconnect your Twitter account
              </Link>
            </>
          ) : (
            error
          )
        ) : null
      }
      extra={taskAmount}
      actions={
        <ActionBlock
          twitterInfo={twitterInfo}
          isLoading={isLoading}
          action={isTwitterViewsTask ? postOnX : follow}
          isLoadingFollow={isLoadingFollow}
          isComplete={isComplete}
          hasError={!!error}
          isTwitterViewsTask={isTwitterViewsTask}
        />
      }
    />
  )
}

export default TwitterTask

const Inner = styled(Box)`
  padding-top: 2rem;
  min-height: 200px;
  img.circle {
    border-radius: 50%;
    width: 64px;
    height: 64px;
  }
  span.screen-name {
    padding-top: 0.5rem;
    font-size: 1.5rem;
  }
  span.username {
    opacity: 0.5;
  }
  @media screen and (max-width: 1000px) {
    padding-top: 1rem;
    min-height: 120px;
  }
`

const CheckContainer = styled(Box)<{ isVisible: boolean }>`
  max-width: 0;
  overflow: hidden;
  opacity: 0;
  transition: 0.3s ease-in-out;
  ${({ isVisible }) =>
    isVisible &&
    css`
      opacity: 1;
      max-width: 28px;
      padding-right: 8px;
    `}
`

function ActionBlock({
  twitterInfo,
  isLoading,
  isLoadingFollow,
  action,
  isComplete,
  hasError,
  isTwitterViewsTask,
}: {
  twitterInfo: TwitterInfo | null
  isLoading: boolean
  isLoadingFollow: boolean
  action: () => void | Promise<void>
  isComplete: boolean
  hasError: boolean
  isTwitterViewsTask: boolean
}) {
  const { isLoggedIn } = useIsLoggedIn()
  const user = useSelector(selectUser)
  const { twitterHandle } = user ?? {}

  const blockedTwitterFollowMission =
    !isTwitterViewsTask &&
    user?.metadata?.blockedMissions?.find(
      (blockedMission) =>
        blockedMission.taskType === 'twitter_follow' &&
        new Date(blockedMission.blockedUntil) > new Date(),
    )

  const refetchUpdateUser = useRefetchUpdateUser()
  useEffect(() => {
    // refetch on unmount so the blocked message only displays if the dismissed the mission modal and opens it again
    // otherwise if we refetch on action, the user will see the error message from the API + the blocked until component
    return () => {
      refetchUpdateUser()
    }
  }, [refetchUpdateUser])

  return (
    <>
      {hasError || isTwitterViewsTask || !twitterInfo ? (
        <></>
      ) : (
        <Inner c>
          {isLoading ? (
            <AnimatedLoader onlyWhite />
          ) : twitterInfo ? (
            <>
              <InnerBlock twitterInfo={twitterInfo} />
            </>
          ) : null}
        </Inner>
      )}
      {!isLoggedIn ? (
        <ConnectWalletButton />
      ) : !twitterHandle ? (
        <TwitterConnectButton label="Connect X" />
      ) : blockedTwitterFollowMission ? (
        <BlockedUntil blockedUntil={blockedTwitterFollowMission.blockedUntil} />
      ) : (
        <ThemedButton
          $styleType="primary"
          onClick={action}
          disabled={isLoading || isLoadingFollow || isComplete || hasError}
        >
          {isComplete && <ConfettiExplosion zIndex={10000} />}
          <CheckContainer isVisible={isComplete}>
            <Checkbox />
          </CheckContainer>
          {isLoadingFollow ? (
            <AnimatedLoader size={14} />
          ) : isComplete ? (
            isTwitterViewsTask ? (
              'Counting Views'
            ) : (
              'Following'
            )
          ) : isTwitterViewsTask ? (
            'Post on X'
          ) : (
            'Follow on X'
          )}
        </ThemedButton>
      )}
    </>
  )
}

function InnerBlock({ twitterInfo }: { twitterInfo: TwitterInfo }) {
  const { profile_image_url, username, verified, name } = twitterInfo
  return (
    <Box dc c g={8}>
      <img src={profile_image_url} alt={username} className="circle" />
      <span className="screen-name">{name}</span>
      <a
        href={'https://twitter.com/' + username}
        target="_blank"
        rel="noreferrer"
      >
        <span className="username">@{username}</span>
      </a>
      {verified && <span>Verified</span>}
    </Box>
  )
}

function BlockedUntil({ blockedUntil }: { blockedUntil: string }) {
  return (
    <span>
      You will be able to verify this task again in{' '}
      {humanizeDuration(new Date(blockedUntil).getTime() - Date.now(), {
        language: 'en',
        units: ['h', 'm'],
        round: true,
        largest: 1,
      })}
    </span>
  )
}
